import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ShieldCheck, MapPin, Ban, Flag, AlertTriangle, MessageSquareWarning, Phone, HeartHandshake } from "lucide-react";
import { BrandLogo } from "../components/BrandLogo";
import { LEGAL_ENABLED } from "../lib/flags";

const SECTIONS = [
  { id: "before", icon: ShieldCheck, title: "Before you meet", items: [
    "Chat in DateBid first. Get a feel for the person before you agree to meet.",
    "Look at their profile, photos, and reviews. If details don't add up, ask — or move on.",
    "Tell a friend who you are meeting, where, and when, and agree a time to check in.",
    "Keep your home address, workplace, financial details, and ID documents private.",
  ]},
  { id: "date", icon: MapPin, title: "On the date", items: [
    "Meet in a busy public place and stay in public on a first date.",
    "Get yourself there and back. You decide when to leave.",
    "Watch your drink and your pace. Never leave your drink unattended.",
    "Trust your instincts. If something feels wrong, leave. You don't owe anyone an explanation.",
  ]},
  { id: "consent", icon: HeartHandshake, title: "Consent and boundaries", items: [
    "Accepting a Date Offer, receiving a gift, or unlocking a conversation is never consent to anything physical or sexual.",
    "You can say no, change your mind, or end a date at any time.",
    "No one may ask for intimate photos, sexual activity, or any service as part of a Date Offer. Report anyone who does.",
  ]},
  { id: "harassment", icon: MessageSquareWarning, title: "Harassment", items: [
    "Harassment includes threats, insults, repeated unwanted messages, sexual comments you didn't ask for, and pressure after you said no.",
    "You don't have to reply. Block the member, then report them so our team can act.",
    "Threats to share your private photos or information are serious — report them and keep a screenshot.",
  ]},
  { id: "scams", icon: AlertTriangle, title: "Scams and suspicious activity", items: [
    "Never send money, gift cards, crypto, or bank details to someone you met on DateBid — whatever the story.",
    "DateBid never holds a Date Offer amount. Anyone asking you to pay one in advance, through a link, or to a third party is running a scam.",
    "Never buy credits for someone else or share your login. DateBid staff will never ask for your password or sign-in code.",
    "Be wary of anyone who professes strong feelings fast, refuses to meet, has an urgent emergency, or pushes you to move off DateBid right away.",
    "Profiles with stolen-looking photos, copy-paste messages, or links to other sites should be reported.",
  ]},
];

const STEPS = [
  { icon: Ban, title: "Block", body: "Open the member's profile and choose Block. They can no longer contact you." },
  { icon: Flag, title: "Report", body: "On their profile choose Report, pick what it's about (profile, photo, messages, or something else), and describe what happened. Every report reaches our moderation team." },
  { icon: ShieldCheck, title: "What happens next", body: "We review the report and may remove content, warn, suspend, or ban the account. We don't tell the other member who reported them." },
];

const Item = ({ text, i, id }) => (
  <li data-testid={`safety-${id}-item-${i}`} className="flex gap-3 text-sm text-[#D8D2C9] leading-relaxed">
    <span className="mt-2 h-1.5 w-1.5 rounded-full bg-[#D4AF37] shrink-0" />{text}
  </li>
);

const Section = ({ s }) => (
  <section data-testid={`safety-section-${s.id}`} className="rounded-2xl border border-white/8 bg-[#141113]/60 p-6">
    <h2 className="font-serif text-xl text-[#FBF8F3] mb-3 flex items-center gap-2"><s.icon size={18} className="text-[#D4AF37]" />{s.title}</h2>
    <ul className="space-y-2.5">{s.items.map((t, i) => <Item key={i} text={t} i={i} id={s.id} />)}</ul>
  </section>
);

export default function SafetyCenter() {
  useEffect(() => { window.scrollTo(0, 0); document.title = "Safety Center | DateBid"; }, []);
  return (
    <div className="min-h-screen bg-[#0C0A0A] text-[#FBF8F3]" data-testid="safety-center-page">
      <div className="max-w-4xl mx-auto px-5 sm:px-8 py-10">
        <div className="flex items-center justify-between mb-8">
          <Link to="/" data-testid="safety-back-home" className="inline-flex items-center gap-1.5 text-sm text-[#9E978E] hover:text-[#FBF8F3] transition-colors"><ArrowLeft size={16} /> DateBid</Link>
          <BrandLogo variant="wordmarkSm" className="h-8 w-auto" />
        </div>
        <div className="text-[11px] uppercase tracking-[0.22em] text-[#D4AF37] mb-2">Safety Center</div>
        <h1 className="font-serif text-4xl sm:text-5xl leading-tight max-w-2xl" data-testid="safety-title">Date with confidence. We've got your back.</h1>
        <p className="mt-4 text-[#C4BDB2] max-w-2xl leading-relaxed">Practical guidance for meeting people from DateBid, and what to do if something isn't right.</p>

        <div data-testid="safety-emergency" className="mt-8 rounded-2xl border border-[#E58CA0]/40 bg-[#541224]/30 p-5 flex gap-3">
          <Phone size={20} className="text-[#E58CA0] shrink-0 mt-0.5" />
          <p className="text-sm text-[#FBF8F3] leading-relaxed"><strong>In immediate danger?</strong> Call your local emergency number (911 in the US) first. Then report the member in DateBid.</p>
        </div>

        <div className="mt-8 grid gap-5 md:grid-cols-2">{SECTIONS.map((s) => <Section key={s.id} s={s} />)}</div>

        <h2 className="font-serif text-2xl mt-12 mb-4" data-testid="safety-report-heading">Blocking and reporting</h2>
        <div className="grid gap-4 sm:grid-cols-3">
          {STEPS.map((st, i) => (
            <div key={st.title} data-testid={`safety-step-${i}`} className="rounded-2xl border border-[#D4AF37]/20 bg-[#141113]/60 p-5">
              <st.icon size={18} className="text-[#D4AF37] mb-2" />
              <div className="font-semibold text-[#FBF8F3] mb-1">{st.title}</div>
              <p className="text-sm text-[#C4BDB2] leading-relaxed">{st.body}</p>
            </div>
          ))}
        </div>

        <p className="mt-10 text-xs text-[#9E978E] leading-relaxed max-w-3xl" data-testid="safety-limits">
          Profile and photo approval is a basic content check. DateBid does not run criminal background checks and cannot guarantee any member's identity, honesty, or safety. Always use your own judgement.
        </p>
        <div className="mt-6 flex flex-wrap gap-x-5 gap-y-2 text-sm">
          <Link to="/first-date-safety-tips" data-testid="safety-link-tips" className="text-[#D4AF37] hover:underline">First date safety tips</Link>
          {LEGAL_ENABLED && <Link to="/legal/safetypolicy" data-testid="safety-link-policy" className="text-[#D4AF37] hover:underline">Safety Policy</Link>}
          {LEGAL_ENABLED && <Link to="/legal/terms" data-testid="safety-link-terms" className="text-[#D4AF37] hover:underline">Terms of Service</Link>}
        </div>
      </div>
    </div>
  );
}
